import { Card, EmptyState, LoadingSpinner } from '@postscript/components';
import { ReactNode } from 'react';
import useOmniReportingAccess from './hooks/useOmniReportingAccess';

interface Props {
  children: ReactNode;
  /** Safe native content used during loading, errors, and confirmed unavailability. */
  fallback?: ReactNode;
  /** Used only after confirmed unavailability, when no native fallback is provided. */
  unavailableFallback?: ReactNode;
}

export default function OmniReportingGate({
  children,
  fallback,
  unavailableFallback,
}: Props) {
  const { hasOmniTrends, isLoading, isError, refetch } =
    useOmniReportingAccess();

  if (isLoading) {
    return (
      <>{fallback ?? <LoadingSpinner>Loading reports...</LoadingSpinner>}</>
    );
  }

  if (isError) {
    return (
      <>
        <Card>
          <EmptyState
            heading="Unable to load reporting access"
            description="Try again to check which reports are available."
            primaryAction={{ text: 'Try again', onClick: () => refetch() }}
          />
        </Card>
        {fallback}
      </>
    );
  }

  if (!hasOmniTrends) {
    return (
      <>
        {fallback ?? unavailableFallback ?? (
          <Card>
            <EmptyState
              heading="These reports aren't available for this shop"
              description="Your shop's current plan doesn't include these reports."
            />
          </Card>
        )}
      </>
    );
  }

  return <>{children}</>;
}
